import type { GuidedSection, SectionStatusMap } from '@lds-efsp/shared-contracts';

/**
 * The filer's confirm act on one guided section (SPEC-SOR-174 R28) — the ONLY
 * transition anywhere in the wizard that produces a `confirmed` status.
 *
 * Every other writer of `sectionStatuses` can reach `pending` or `prefilled`
 * only: the extraction guard's `advanceSectionStatuses` cannot express
 * `confirmed` at all (R27), the court-change reset drops all four sections
 * (R17), and the case-change revert drops three (R18). That leaves this
 * function as the single edge into the value the gates read, which is what
 * makes "readiness alone never promotes a section" a fact about the code rather
 * than a convention every future writer has to be told about.
 *
 * Pure and separate from the context for the same reason the R17 and R18
 * transitions are: the rule is exhaustively testable over the status ×
 * provenance space, and the context callback is then a `setWizardState` around
 * it with nothing of its own to get wrong.
 *
 * Type-only on `@lds-efsp/shared-contracts`, matching its two sibling
 * transitions, so the spec property probes under `internal/proptests` can
 * compile this source directly.
 */

/** The two persisted guided-section fields this transition rewrites. */
export interface FilerConfirmState {
  sectionStatuses?: SectionStatusMap;
  filerConfirmedSections?: GuidedSection[];
}

/**
 * Record the filer's confirm act on `section`.
 *
 * Both halves move together and only together — the status the gates read and
 * the provenance that justifies it. Writing the status alone would leave a
 * `confirmed` section that no filer act accounts for, which is precisely what
 * R28 exists to forbid; writing the provenance alone would leave the act
 * unhonoured.
 *
 * Idempotent: re-confirming an already-confirmed section returns the input
 * object unchanged (by identity), so a double-click can neither duplicate the
 * provenance entry nor churn wizard state into a redundant auto-save.
 *
 * Readiness and the confirmation ladder are deliberately NOT re-checked here.
 * `confirmOffered` (R2/R31) decides whether the control is offered at all, and
 * a second copy of that decision at the write site is a second implementation
 * free to drift from the one the control renders from.
 */
export function confirmSectionState(
  prev: FilerConfirmState,
  section: GuidedSection,
): FilerConfirmState {
  const priorActs = prev.filerConfirmedSections ?? [];
  // The no-op case requires BOTH halves to already be in place. Keying it on
  // either one alone would let a half-written draft — a status with no act, or
  // an act with no status — survive the filer pressing Confirm, which is the
  // one input that should always be able to put R28 back into agreement.
  if (prev.sectionStatuses?.[section] === 'confirmed' && priorActs.includes(section)) {
    return prev;
  }

  return {
    sectionStatuses: { ...prev.sectionStatuses, [section]: 'confirmed' },
    filerConfirmedSections: priorActs.includes(section) ? priorActs : [...priorActs, section],
  };
}
